import {
  type DynamicClient,
  InvalidParamError,
} from '@dynamic-labs-sdk/client';
import type {
  ExecuteSwapTransactionParams,
  ExecuteSwapTransactionResult,
} from '@dynamic-labs-sdk/client/core';

import { createWalletClientForWalletAccount } from '../../viem/createWalletClientForWalletAccount';
import { approveErc20IfRequiredAndWaitReceipt } from '../approveErc20IfRequiredAndWaitReceipt';
import { isEvmWalletAccount } from '../isEvmWalletAccount';

/**
 * Executes an EVM checkout transaction by handling optional ERC-20 approval
 * and sending the main swap transaction on-chain.
 *
 * 1. Validates that the wallet account is an EVM wallet account
 * 2. If the signing payload includes an `evmApproval`, checks the current
 *    ERC-20 allowance and sends an approve transaction when insufficient
 * 3. Sends the main transaction using the viem wallet client
 *
 * @param params.signingPayload - The chain-aware signing payload containing `evmTransaction` and optional `evmApproval`.
 * @param params.walletAccount - The wallet account used to sign and send transactions.
 * @param params.onStepChange - Optional callback invoked with `'approval'` or `'transaction'` as each step begins.
 * @param client - The Dynamic client instance.
 * @returns The main transaction hash and, if an approval was sent, the approval transaction hash.
 * @not-instrumented
 */
export const evmExecuteSwapTransaction = async (
  { onStepChange, signingPayload, walletAccount }: ExecuteSwapTransactionParams,
  client: DynamicClient
): Promise<ExecuteSwapTransactionResult> => {
  if (!isEvmWalletAccount(walletAccount)) {
    throw new InvalidParamError('Wallet account is not an EVM wallet account');
  }

  const { evmTransaction, evmApproval } = signingPayload;

  if (!evmTransaction) {
    throw new InvalidParamError(
      'Signing payload is missing EVM transaction data'
    );
  }

  if (evmApproval) {
    onStepChange?.('approval');

    await approveErc20IfRequiredAndWaitReceipt(
      {
        amount: evmApproval.amount,
        spenderAddress: evmApproval.spenderAddress,
        tokenAddress: evmApproval.tokenAddress,
        walletAccount,
      },
      client
    );
  }

  onStepChange?.('transaction');

  const walletClient = await createWalletClientForWalletAccount(
    { walletAccount },
    client
  );

  // sendTransaction expects gasPrice or maxFeePerGas and maxPriorityFeePerGas
  let gasParams: Record<string, bigint> = {};
  if (evmTransaction.maxFeePerGas && evmTransaction.maxPriorityFeePerGas) {
    gasParams = {
      maxFeePerGas: BigInt(evmTransaction.maxFeePerGas),
      maxPriorityFeePerGas: BigInt(evmTransaction.maxPriorityFeePerGas),
    };
  } else {
    gasParams = {
      ...(evmTransaction.gasPrice && {
        gasPrice: BigInt(evmTransaction.gasPrice),
      }),
    };
  }

  const transactionHash = await walletClient.sendTransaction({
    data: evmTransaction.data as `0x${string}`,
    to: evmTransaction.to as `0x${string}`,
    value: evmTransaction.value ? BigInt(evmTransaction.value) : 0n,
    ...(evmTransaction.gasLimit && { gas: BigInt(evmTransaction.gasLimit) }),
    ...gasParams,
    ...(evmTransaction.nonce && { nonce: evmTransaction.nonce }),
  });

  return { transactionHash };
};
